import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  User, Music, Globe, MapPin, Camera, Save, Loader2,
  Link as LinkIcon, CheckCircle, AlertCircle, X, Upload,
  ChevronDown, ChevronUp, FileText, Mic, Users, Target, Plus, Trash2
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './ProfileEditor.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (url) => {
  if (!url) return '';
  return getImageUrl(url) || '';
};

const GENRE_OPTIONS = [
  'Electronic', 'House', 'Techno', 'Deep House', 'Tech House',
  'Progressive House', 'Melodic Techno', 'Minimal', 'Trance',
  'Drum & Bass', 'Dubstep', 'Future Bass', 'Hip Hop', 'R&B',
  'Pop', 'Rock', 'Indie', 'Alternative', 'Ambient', 'Downtempo',
  'Disco', 'Funk', 'Soul', 'Jazz', 'Classical', 'World', 'Other'
];

const ARTIST_TYPES = [
  'Solo Artist', 'DJ', 'Producer', 'DJ/Producer', 'Band', 'Group', 
  'Singer/Songwriter', 'Composer', 'Other'
];

const COUNTRIES = [
  'Canada', 'United States', 'United Kingdom', 'Germany', 'France', 
  'Spain', 'Italy', 'Netherlands', 'Belgium', 'Australia', 'Sweden',
  'Norway', 'Denmark', 'Poland', 'Portugal', 'Brazil', 'Mexico', 
  'Argentina', 'Japan', 'South Korea', 'India', 'South Africa', 'Other'
];

const ProfileEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [profile, setProfile] = useState(null);
  const [artistName, setArtistName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [pressKitOpen, setPressKitOpen] = useState(true);
  
  const profileImageRef = useRef(null);
  const bannerImageRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    artist_type: '',
    main_genre: '',
    secondary_genres: [],
    bio: '',
    short_bio: '',
    medium_bio: '',
    pronunciation: '',
    brand_adjectives: [],
    comparable_artists: '',
    visual_references: '',
    country: '',
    city: '',
    profile_image: '',
    banner_image: '',
    social_links: {
      website_url: '',
      facebook_url: '',
      x_handle: '',
      instagram_handle: '',
      youtube_channel: '',
      tiktok_username: '',
      soundcloud_url: '',
      spotify_url: '',
      beatport_url: '',
      bandcamp_url: '',
      apple_music_url: '',
      deezer_url: '',
      amazon_music_url: '',
      audiomack_url: '',
    },
    years_experience: '',
    events_played: '',
    hours_mixed: '',
    tech_rider: '',
    // DJ & Promo
    beatport_genre: '',
    secondary_vibe: '',
    dj_pitch: '',
    priority_djs: '',
    existing_dj_support: '',
    target_radio_shows: '',
    club_markets: '',
    // Team contacts
    team_contacts: [],
  });

  const getToken = () => localStorage.getItem('artistToken');

  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/me`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setArtistName(data.account?.artist_name || '');
        
        if (data.profile) {
          setProfile(data.profile);
          const sl = data.profile.social_links || {};
          const si = data.profile.streaming_ids || {};
          // Merge old streaming_ids into social_links as fallbacks
          setFormData({
            artist_type: data.profile.artist_type || '',
            main_genre: data.profile.main_genre || '',
            secondary_genres: data.profile.secondary_genres || [],
            bio: data.profile.bio || '',
            short_bio: data.profile.short_bio || '',
            medium_bio: data.profile.medium_bio || '',
            pronunciation: data.profile.pronunciation || '',
            brand_adjectives: data.profile.brand_adjectives || [],
            comparable_artists: data.profile.comparable_artists || '',
            visual_references: data.profile.visual_references || '',
            country: data.profile.country || '',
            city: data.profile.city || '',
            profile_image: data.profile.profile_image || '',
            banner_image: data.profile.banner_image || '',
            social_links: {
              website_url: sl.website_url || '',
              facebook_url: sl.facebook_url || '',
              x_handle: sl.x_handle || '',
              instagram_handle: sl.instagram_handle || '',
              youtube_channel: sl.youtube_channel || '',
              tiktok_username: sl.tiktok_username || '',
              soundcloud_url: sl.soundcloud_url || (si.soundcloud_id ? `https://soundcloud.com/${si.soundcloud_id}` : ''),
              spotify_url: sl.spotify_url || (si.spotify_id ? `https://open.spotify.com/artist/${si.spotify_id}` : ''),
              beatport_url: sl.beatport_url || '',
              bandcamp_url: sl.bandcamp_url || '',
              apple_music_url: sl.apple_music_url || (si.apple_music_id ? `https://music.apple.com/artist/${si.apple_music_id}` : ''),
              deezer_url: sl.deezer_url || (si.deezer_id ? `https://deezer.com/artist/${si.deezer_id}` : ''),
              amazon_music_url: sl.amazon_music_url || (si.amazon_id ? `https://music.amazon.com/artists/${si.amazon_id}` : ''),
              audiomack_url: sl.audiomack_url || (si.audiomack_id ? `https://audiomack.com/${si.audiomack_id}` : ''),
            },
            years_experience: data.profile.years_experience ?? '',
            events_played: data.profile.events_played ?? '',
            hours_mixed: data.profile.hours_mixed ?? '',
            tech_rider: data.profile.tech_rider || '',
            beatport_genre: data.profile.beatport_genre || '',
            secondary_vibe: data.profile.secondary_vibe || '',
            dj_pitch: data.profile.dj_pitch || '',
            priority_djs: data.profile.priority_djs || '',
            existing_dj_support: data.profile.existing_dj_support || '',
            target_radio_shows: data.profile.target_radio_shows || '',
            club_markets: data.profile.club_markets || '',
            team_contacts: data.profile.team_contacts || [],
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSecondaryGenreToggle = (genre) => {
    const current = formData.secondary_genres || [];
    if (current.includes(genre)) {
      handleChange('secondary_genres', current.filter(g => g !== genre));
    } else if (current.length < 5) {
      handleChange('secondary_genres', [...current, genre]);
    } else {
      toast.error('Maximum 5 secondary genres allowed');
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(type);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await fetch(`${API_URL}/api/artist/upload/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        handleChange(type, data.file_url);
        toast.success(`${type === 'profile_image' ? 'Profile' : 'Banner'} image uploaded!`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Clean up empty nested objects
      const cleanData = { ...formData };
      
      // Convert empty string int fields to null
      for (const intField of ['years_experience', 'events_played', 'hours_mixed']) {
        if (cleanData[intField] === '' || cleanData[intField] === null) {
          cleanData[intField] = null;
        } else {
          cleanData[intField] = parseInt(cleanData[intField], 10) || null;
        }
      }

      // Only include social_links if at least one is filled
      const hasSocialLinks = Object.values(formData.social_links).some(v => v);
      if (!hasSocialLinks) {
        delete cleanData.social_links;
      }

      const res = await fetch(`${API_URL}/api/artist/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        setHasChanges(false);
        toast.success('Profile saved successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getCompletionPercentage = () => {
    const fields = [
      formData.artist_type,
      formData.main_genre,
      formData.bio,
      formData.country,
      formData.profile_image
    ];
    const filled = fields.filter(f => f && f.trim()).length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <div className="profile-editor loading">
        <Loader2 size={32} className="spinner" />
        <p>Loading your profile...</p>
      </div>
    );
  }

  const completionPct = getCompletionPercentage();

  return (
    <div className="profile-editor">
      <div className="editor-header">
        <div className="header-info">
          <h1>Edit Profile</h1>
          <p>Complete your profile to be featured on the AMR website</p>
        </div>
        <div className="header-actions">
          <div className={`completion-badge ${completionPct === 100 ? 'complete' : ''}`}>
            {completionPct === 100 ? (
              <><CheckCircle size={16} /> Profile Complete</>
            ) : (
              <><AlertCircle size={16} /> {completionPct}% Complete</>
            )}
          </div>
          <button 
            className="save-btn"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            Save Changes
          </button>
        </div>
      </div>

      <div className="editor-content">
        {/* Images Section */}
        <section className="editor-section images-section">
          <h2><Camera size={20} /> Profile Images</h2>
          
          <div className="images-grid">
            <div className="image-upload-box">
              <label>Profile Photo</label>
              <div 
                className={`image-preview profile ${uploading === 'profile_image' ? 'uploading' : ''}`}
                onClick={() => profileImageRef.current?.click()}
                style={formData.profile_image ? { backgroundImage: `url(${getFileUrl(formData.profile_image)})` } : {}}
              >
                {uploading === 'profile_image' ? (
                  <Loader2 size={32} className="spinner" />
                ) : !formData.profile_image && (
                  <>
                    <Upload size={28} />
                    <span>Upload Photo</span>
                    <span className="hint">Square, min 800x800px</span>
                  </>
                )}
                <input
                  ref={profileImageRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleImageUpload('profile_image', e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>
              {formData.profile_image && (
                <button className="remove-image" onClick={() => handleChange('profile_image', '')}>
                  <X size={14} /> Remove
                </button>
              )}
            </div>

            <div className="image-upload-box wide">
              <label>Banner Image</label>
              <div 
                className={`image-preview banner ${uploading === 'banner_image' ? 'uploading' : ''}`}
                onClick={() => bannerImageRef.current?.click()}
                style={formData.banner_image ? { backgroundImage: `url(${getFileUrl(formData.banner_image)})` } : {}}
              >
                {uploading === 'banner_image' ? (
                  <Loader2 size={32} className="spinner" />
                ) : !formData.banner_image && (
                  <>
                    <Upload size={28} />
                    <span>Upload Banner</span>
                    <span className="hint">Wide, min 1920x600px</span>
                  </>
                )}
                <input
                  ref={bannerImageRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleImageUpload('banner_image', e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>
              {formData.banner_image && (
                <button className="remove-image" onClick={() => handleChange('banner_image', '')}>
                  <X size={14} /> Remove
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Basic Info Section */}
        <section className="editor-section">
          <h2><User size={20} /> Basic Information</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Artist Name</label>
              <input
                type="text"
                value={artistName}
                disabled
                className="disabled"
              />
              <span className="field-hint">Contact staff to change your artist name</span>
            </div>

            <div className="form-group">
              <label>Artist Type *</label>
              <select
                value={formData.artist_type}
                onChange={(e) => handleChange('artist_type', e.target.value)}
              >
                <option value="">Select type</option>
                {ARTIST_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Country *</label>
              <select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              >
                <option value="">Select country</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Your city"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>Full Bio *</label>
            <textarea
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              placeholder="Tell us about yourself, your music journey, and what inspires you..."
              rows={5}
              maxLength={2000}
            />
            <span className="char-count">{formData.bio?.length || 0} / 2000</span>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Short Bio (50 words)</label>
              <textarea
                value={formData.short_bio}
                onChange={(e) => handleChange('short_bio', e.target.value)}
                placeholder="One-liner or social media bio..."
                rows={2}
                maxLength={350}
                data-testid="input-short-bio"
              />
              <span className="char-count">{(formData.short_bio || '').split(/\s+/).filter(Boolean).length} words</span>
            </div>

            <div className="form-group">
              <label>Medium Bio (120 words)</label>
              <textarea
                value={formData.medium_bio}
                onChange={(e) => handleChange('medium_bio', e.target.value)}
                placeholder="Platform profile bio — Spotify, Beatport, etc."
                rows={2}
                maxLength={800}
                data-testid="input-medium-bio"
              />
              <span className="char-count">{(formData.medium_bio || '').split(/\s+/).filter(Boolean).length} words</span>
            </div>
          </div>
        </section>

        {/* Identity & Branding */}
        <section className="editor-section">
          <h2><Mic size={20} /> Identity & Branding</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>Name Pronunciation</label>
              <input
                type="text"
                value={formData.pronunciation}
                onChange={(e) => handleChange('pronunciation', e.target.value)}
                placeholder='e.g. "ah-REEV" or "MAHS-tah soul"'
                data-testid="input-pronunciation"
              />
              <span className="field-hint">How should hosts/DJs say your name?</span>
            </div>

            <div className="form-group">
              <label>Comparable Artists / RIYL (3-5)</label>
              <input
                type="text"
                value={formData.comparable_artists}
                onChange={(e) => handleChange('comparable_artists', e.target.value)}
                placeholder="e.g. Fisher, Chris Lake, Dom Dolla"
                data-testid="input-comparable-artists"
              />
              <span className="field-hint">Recommended If You Like — helps with playlist pitching</span>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Brand Adjectives (pick up to 5)</label>
            <div className="genre-chips">
              {['Energetic', 'Dark', 'Melodic', 'Raw', 'Groovy', 'Atmospheric', 'Uplifting', 'Deep', 'Hypnotic', 'Funky', 'Minimal', 'Powerful', 'Smooth', 'Organic', 'Futuristic', 'Soulful', 'Cinematic', 'Bold', 'Playful', 'Edgy'].map(adj => (
                <button
                  key={adj}
                  type="button"
                  className={`genre-chip ${formData.brand_adjectives?.includes(adj) ? 'selected' : ''}`}
                  onClick={() => {
                    const current = formData.brand_adjectives || [];
                    if (current.includes(adj)) {
                      handleChange('brand_adjectives', current.filter(a => a !== adj));
                    } else if (current.length < 5) {
                      handleChange('brand_adjectives', [...current, adj]);
                    }
                  }}
                  data-testid={`adj-${adj.toLowerCase()}`}
                >
                  {adj}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group full-width">
            <label>Visual References / Mood Notes</label>
            <textarea
              value={formData.visual_references}
              onChange={(e) => handleChange('visual_references', e.target.value)}
              placeholder="Describe your visual identity — color palette, mood, references for artwork and marketing..."
              rows={3}
              data-testid="input-visual-references"
            />
          </div>
        </section>

        {/* Team Contacts */}
        <section className="editor-section">
          <h2><Users size={20} /> Team Contacts</h2>
          <p className="section-desc">Add your manager, publicist, booking agent, or other team members</p>

          {(formData.team_contacts || []).map((contact, idx) => (
            <div key={idx} className="form-grid form-grid-team" data-testid={`team-contact-${idx}`}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={contact.name || ''}
                  onChange={(e) => {
                    const updated = [...formData.team_contacts];
                    updated[idx] = { ...updated[idx], name: e.target.value };
                    handleChange('team_contacts', updated);
                  }}
                  placeholder="Full name"
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <input
                  type="text"
                  value={contact.role || ''}
                  onChange={(e) => {
                    const updated = [...formData.team_contacts];
                    updated[idx] = { ...updated[idx], role: e.target.value };
                    handleChange('team_contacts', updated);
                  }}
                  placeholder="e.g. Manager, Publicist, Booking"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={contact.email || ''}
                  onChange={(e) => {
                    const updated = [...formData.team_contacts];
                    updated[idx] = { ...updated[idx], email: e.target.value };
                    handleChange('team_contacts', updated);
                  }}
                  placeholder="email@example.com"
                />
              </div>
              <button
                type="button"
                className="remove-image"
                style={{ alignSelf: 'flex-end', marginBottom: 4 }}
                onClick={() => {
                  handleChange('team_contacts', formData.team_contacts.filter((_, i) => i !== idx));
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="genre-chip selected"
            style={{ marginTop: 8 }}
            onClick={() => handleChange('team_contacts', [...(formData.team_contacts || []), { name: '', role: '', email: '' }])}
            data-testid="add-team-contact"
          >
            <Plus size={14} /> Add Team Member
          </button>
        </section>

        {/* Genre Section */}
        <section className="editor-section">
          <h2><Music size={20} /> Genre</h2>
          
          <div className="form-group">
            <label>Primary Genre *</label>
            <select
              value={formData.main_genre}
              onChange={(e) => handleChange('main_genre', e.target.value)}
            >
              <option value="">Select genre</option>
              {GENRE_OPTIONS.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Secondary Genres (up to 5)</label>
            <div className="genre-chips">
              {GENRE_OPTIONS.filter(g => g !== formData.main_genre).map(genre => (
                <button
                  key={genre}
                  type="button"
                  className={`genre-chip ${formData.secondary_genres?.includes(genre) ? 'selected' : ''}`}
                  onClick={() => handleSecondaryGenreToggle(genre)}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Press Kit Info Section */}
        <section className="editor-section presskit-section">
          <button
            type="button"
            className="section-collapse-toggle"
            onClick={() => setPressKitOpen(!pressKitOpen)}
            data-testid="toggle-presskit"
          >
            <div className="section-collapse-left">
              <FileText size={20} />
              <div>
                <h2>Press Kit Info</h2>
                <span className="section-subtitle">This information appears on your public press kit page</span>
              </div>
            </div>
            {pressKitOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {pressKitOpen && (
            <div className="section-collapse-body" data-testid="presskit-section">
              <div className="form-grid form-grid-3col">
                <div className="form-group">
                  <label>Years of Experience</label>
                  <input
                    type="number"
                    value={formData.years_experience}
                    onChange={(e) => handleChange('years_experience', e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="e.g. 8"
                    min="0"
                    max="99"
                    data-testid="input-years-experience"
                  />
                </div>

                <div className="form-group">
                  <label>Events Played</label>
                  <input
                    type="number"
                    value={formData.events_played}
                    onChange={(e) => handleChange('events_played', e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="e.g. 250"
                    min="0"
                    data-testid="input-events-played"
                  />
                </div>

                <div className="form-group">
                  <label>Hours Mixed</label>
                  <input
                    type="number"
                    value={formData.hours_mixed}
                    onChange={(e) => handleChange('hours_mixed', e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="e.g. 500"
                    min="0"
                    data-testid="input-hours-mixed"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Technical Rider / Gear Requirements</label>
                <textarea
                  value={formData.tech_rider}
                  onChange={(e) => handleChange('tech_rider', e.target.value)}
                  placeholder="e.g. 2x Pioneer CDJ-3000, DJM-900NXS2, RMX-1000, Laptop stand. All equipment must be in working condition and tested before performance."
                  rows={4}
                  data-testid="input-tech-rider"
                />
              </div>
            </div>
          )}
        </section>

        {/* DJ & Promo Strategy */}
        <section className="editor-section">
          <h2><Target size={20} /> DJ & Promo Strategy</h2>
          <p className="section-desc">Help the AMR promo team position your music for maximum impact</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Beatport Primary Genre</label>
              <input
                type="text"
                value={formData.beatport_genre}
                onChange={(e) => handleChange('beatport_genre', e.target.value)}
                placeholder="e.g. Melodic House & Techno"
                data-testid="input-beatport-genre"
              />
            </div>

            <div className="form-group">
              <label>Secondary Style / Vibe</label>
              <input
                type="text"
                value={formData.secondary_vibe}
                onChange={(e) => handleChange('secondary_vibe', e.target.value)}
                placeholder="e.g. Peak-time afro, organic deep"
                data-testid="input-secondary-vibe"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>DJ Pitch (one-liner)</label>
            <input
              type="text"
              value={formData.dj_pitch}
              onChange={(e) => handleChange('dj_pitch', e.target.value)}
              placeholder='e.g. "Driving melodic techno meets organic percussion — built for sunrise sets"'
              data-testid="input-dj-pitch"
            />
            <span className="field-hint">What you'd say to a DJ in 10 seconds to get them to play your track</span>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Priority DJs to Target</label>
              <textarea
                value={formData.priority_djs}
                onChange={(e) => handleChange('priority_djs', e.target.value)}
                placeholder="List DJs you'd most like to play your music, one per line"
                rows={3}
                data-testid="input-priority-djs"
              />
            </div>

            <div className="form-group">
              <label>Existing DJ Support</label>
              <textarea
                value={formData.existing_dj_support}
                onChange={(e) => handleChange('existing_dj_support', e.target.value)}
                placeholder="DJs who already play your tracks, one per line"
                rows={3}
                data-testid="input-existing-dj-support"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Target Radio Shows / Podcasts</label>
              <textarea
                value={formData.target_radio_shows}
                onChange={(e) => handleChange('target_radio_shows', e.target.value)}
                placeholder="e.g. Cercle, Anjunadeep Edition, Afterlife Voyage"
                rows={2}
                data-testid="input-target-radio"
              />
            </div>

            <div className="form-group">
              <label>Club Markets</label>
              <textarea
                value={formData.club_markets}
                onChange={(e) => handleChange('club_markets', e.target.value)}
                placeholder="Cities/regions where your music performs best, e.g. Ibiza, Berlin, Miami"
                rows={2}
                data-testid="input-club-markets"
              />
            </div>
          </div>
        </section>

        {/* Online Presence — unified social & streaming links */}
        <section className="editor-section">
          <h2><Globe size={20} /> Online Presence</h2>
          <p className="section-desc">Add your profile links — these will appear on your public profile and link page</p>
          
          <h3 className="subsection-title"><Music size={16} /> Music Platforms</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Spotify</label>
              <input
                type="url"
                value={formData.social_links.spotify_url}
                onChange={(e) => handleNestedChange('social_links', 'spotify_url', e.target.value)}
                placeholder="https://open.spotify.com/artist/..."
              />
            </div>

            <div className="form-group">
              <label>Apple Music</label>
              <input
                type="url"
                value={formData.social_links.apple_music_url}
                onChange={(e) => handleNestedChange('social_links', 'apple_music_url', e.target.value)}
                placeholder="https://music.apple.com/artist/..."
              />
            </div>

            <div className="form-group">
              <label>SoundCloud</label>
              <input
                type="url"
                value={formData.social_links.soundcloud_url}
                onChange={(e) => handleNestedChange('social_links', 'soundcloud_url', e.target.value)}
                placeholder="https://soundcloud.com/yourusername"
              />
            </div>

            <div className="form-group">
              <label>Beatport</label>
              <input
                type="url"
                value={formData.social_links.beatport_url}
                onChange={(e) => handleNestedChange('social_links', 'beatport_url', e.target.value)}
                placeholder="https://beatport.com/artist/..."
              />
            </div>

            <div className="form-group">
              <label>Bandcamp</label>
              <input
                type="url"
                value={formData.social_links.bandcamp_url}
                onChange={(e) => handleNestedChange('social_links', 'bandcamp_url', e.target.value)}
                placeholder="https://yourusername.bandcamp.com"
              />
            </div>

            <div className="form-group">
              <label>Deezer</label>
              <input
                type="url"
                value={formData.social_links.deezer_url}
                onChange={(e) => handleNestedChange('social_links', 'deezer_url', e.target.value)}
                placeholder="https://deezer.com/artist/..."
              />
            </div>

            <div className="form-group">
              <label>Amazon Music</label>
              <input
                type="url"
                value={formData.social_links.amazon_music_url}
                onChange={(e) => handleNestedChange('social_links', 'amazon_music_url', e.target.value)}
                placeholder="https://music.amazon.com/artists/..."
              />
            </div>

            <div className="form-group">
              <label>Audiomack</label>
              <input
                type="url"
                value={formData.social_links.audiomack_url}
                onChange={(e) => handleNestedChange('social_links', 'audiomack_url', e.target.value)}
                placeholder="https://audiomack.com/yourusername"
              />
            </div>
          </div>

          <h3 className="subsection-title"><LinkIcon size={16} /> Social Media</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                value={formData.social_links.website_url}
                onChange={(e) => handleNestedChange('social_links', 'website_url', e.target.value)}
                placeholder="https://yourwebsite.com"
              />
            </div>

            <div className="form-group">
              <label>Instagram</label>
              <input
                type="text"
                value={formData.social_links.instagram_handle}
                onChange={(e) => handleNestedChange('social_links', 'instagram_handle', e.target.value)}
                placeholder="@yourusername"
              />
            </div>

            <div className="form-group">
              <label>X (Twitter)</label>
              <input
                type="text"
                value={formData.social_links.x_handle}
                onChange={(e) => handleNestedChange('social_links', 'x_handle', e.target.value)}
                placeholder="@yourusername"
              />
            </div>

            <div className="form-group">
              <label>TikTok</label>
              <input
                type="text"
                value={formData.social_links.tiktok_username}
                onChange={(e) => handleNestedChange('social_links', 'tiktok_username', e.target.value)}
                placeholder="@yourusername"
              />
            </div>

            <div className="form-group">
              <label>YouTube</label>
              <input
                type="url"
                value={formData.social_links.youtube_channel}
                onChange={(e) => handleNestedChange('social_links', 'youtube_channel', e.target.value)}
                placeholder="https://youtube.com/@channel"
              />
            </div>

            <div className="form-group">
              <label>Facebook</label>
              <input
                type="url"
                value={formData.social_links.facebook_url}
                onChange={(e) => handleNestedChange('social_links', 'facebook_url', e.target.value)}
                placeholder="https://facebook.com/yourpage"
              />
            </div>
          </div>
        </section>
      </div>

      {/* Floating Save Button */}
      {hasChanges && (
        <div className="floating-save">
          <span>You have unsaved changes</span>
          <button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileEditor;
